import React, { createContext, useContext, useMemo, type PropsWithChildren } from 'react';
import type { DocumentContext } from '@/document-render/domain/DocumentContext';
import type { DocumentTypeRef } from '@/document-types/domain/types/DocumentType';

void React;

export type PreviewContextSource = 'scenario' | 'host' | 'api' | 'fallback';

export interface PreviewContextProviderProps extends PropsWithChildren {
  /** Optional host override — highest priority for all document types. */
  value?: DocumentContext | null;
  /** Optional per-document-type resolver supplied by the host. */
  resolveContext?: (ref: DocumentTypeRef) => DocumentContext | null | undefined;
}

interface PreviewContextHostValue {
  hostContext: DocumentContext | null | undefined;
  resolveContext: ((ref: DocumentTypeRef) => DocumentContext | null | undefined) | undefined;
}

const PreviewContextHostContext = createContext<PreviewContextHostValue | null>(null);

export const PreviewContextProvider = ({ children, value, resolveContext }: PreviewContextProviderProps) => {
  const hostValue = useMemo(
    () => ({
      hostContext: value,
      resolveContext
    }),
    [resolveContext, value]
  );

  // Mirror HostConfigProvider: omit a new layer when the host did not pass overrides.
  // Federated TemplateEditor wraps an empty PreviewContextProvider; without passthrough that
  // shadows a host-supplied branding context and company logos never appear after upload.
  const hasHostOverride = value !== undefined || resolveContext !== undefined;
  if (!hasHostOverride) {
    return <>{children}</>;
  }

  return <PreviewContextHostContext.Provider value={hostValue}>{children}</PreviewContextHostContext.Provider>;
};

export const usePreviewContextHost = (): PreviewContextHostValue => {
  const ctx = useContext(PreviewContextHostContext);
  return ctx ?? { hostContext: undefined, resolveContext: undefined };
};
